/**
 * TripsTable — admin trips listing table matching command-centre mockup.
 */
import React, { useState } from "react";
import styled from "styled-components";
import { IconButton, Menu, MenuItem } from "@mui/material";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import TripStatusBadge from "./TripStatusBadge.jsx";
import TripPastDueBadge from "./TripPastDueBadge.jsx";
import TripProgressBar from "./TripProgressBar.jsx";
import { TRIP_STATUS_LABELS } from "../constants/tripStatuses.js";
import {
  formatCapacityFraction,
  formatRouteDescription,
  formatTripDeparture,
  getDriverLabel,
  getOrderCount,
  getTripProgressPercent,
  getVehicleLabel,
} from "../utils/tripFormatters.js";

const ACCENT = "#e3292f";

const TableWrap = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #64748b;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 14px 16px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
  font-size: 14px;
  color: #111827;
`;

const Tr = styled.tr`
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #fafafa;
  }

  &:last-child ${Td} {
    border-bottom: none;
  }
`;

const TripCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
`;

const TripId = styled.span`
  font-weight: 700;
  color: #111827;
`;

const RouteText = styled.span`
  color: #475569;
  font-size: 13px;
  max-width: 260px;
  display: block;
`;

const SubText = styled.span`
  display: block;
  font-size: 12px;
  color: #64748b;
`;

const StatusLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  color: #2563eb;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const EmptyState = styled.div`
  padding: 48px 24px;
  text-align: center;
  color: #64748b;
  font-size: 14px;
`;

const ProgressCell = styled.div`
  min-width: 120px;
`;

const TripsTable = ({ trips = [], onView, onEdit, onManageDrops }) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuTrip, setMenuTrip] = useState(null);

  const openMenu = (event, trip) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuTrip(trip);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuTrip(null);
  };

  if (!trips.length) {
    return (
      <TableWrap>
        <EmptyState>No trips match the current filters.</EmptyState>
      </TableWrap>
    );
  }

  return (
    <TableWrap data-testid="trips-table">
      <Table>
        <thead>
          <tr>
            <Th>Trip</Th>
            <Th>Route</Th>
            <Th>Departure</Th>
            <Th>Vehicle / Driver</Th>
            <Th>Capacity</Th>
            <Th>Progress</Th>
            <Th>Orders</Th>
            <Th>Status</Th>
            <Th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {trips.map((trip) => {
            const tripKey = trip.id || trip._id;
            const progress = getTripProgressPercent(trip);

            return (
              <Tr key={tripKey} onClick={() => onView?.(trip)}>
                <Td>
                  <TripCell>
                    <TripId>{trip.routeCorridor || tripKey}</TripId>
                    <BadgeRow>
                      <TripStatusBadge status={trip.status} />
                      {trip.isDepartureOverdue ? <TripPastDueBadge /> : null}
                    </BadgeRow>
                  </TripCell>
                </Td>
                <Td>
                  <RouteText>{formatRouteDescription(trip.routeCorridor)}</RouteText>
                </Td>
                <Td>{formatTripDeparture(trip.departureDate)}</Td>
                <Td>
                  <SubText>{getVehicleLabel(trip)}</SubText>
                  <SubText>{getDriverLabel(trip)}</SubText>
                </Td>
                <Td>{formatCapacityFraction(trip)}</Td>
                <Td>
                  <ProgressCell>
                    <TripProgressBar value={progress} />
                  </ProgressCell>
                </Td>
                <Td>{getOrderCount(trip)}</Td>
                <Td>
                  <StatusLink
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onView?.(trip);
                    }}
                  >
                    {TRIP_STATUS_LABELS[trip.status] || trip.status}
                  </StatusLink>
                </Td>
                <Td>
                  <IconButton
                    size="small"
                    aria-label="Trip actions"
                    onClick={(event) => openMenu(event, trip)}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Td>
              </Tr>
            );
          })}
        </tbody>
      </Table>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            onView?.(menuTrip);
            closeMenu();
          }}
        >
          View details
        </MenuItem>
        {onEdit ? (
          <MenuItem
            onClick={() => {
              onEdit(menuTrip);
              closeMenu();
            }}
          >
            Edit trip
          </MenuItem>
        ) : null}
        {onManageDrops ? (
          <MenuItem
            onClick={() => {
              onManageDrops(menuTrip);
              closeMenu();
            }}
          >
            Manage drops
          </MenuItem>
        ) : null}
      </Menu>
    </TableWrap>
  );
};

export default TripsTable;
